import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { 
  ChartPieIcon, 
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ReceiptRefundIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { fetchBenefitsDashboardData, computePeriodFinancials } from '../../utils/benefitsFinance';
import { BENEFITS_DASHBOARD_KEY } from '../../utils/queryKeys';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import toast from 'react-hot-toast';

// Import components for educational institution
import BenefitsOverview from '../../components/benefits/BenefitsOverview';
import TeachersList from '../../components/benefits/TeachersList';
import RevenueAnalysisSimplified from '../../components/benefits/RevenueAnalysisSimplified';
import ExpenseTracker from '../../components/benefits/ExpenseTracker';

const BenefitsPage = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Real-time update indicators
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  const tabs = useMemo(
    () => [
      {
        id: 'overview',
        name: t('benefitsTabs.overviewName'),
        icon: ChartPieIcon,
        component: BenefitsOverview,
        description: t('benefitsTabs.overviewDesc'),
      },
      {
        id: 'teachers',
        name: t('benefitsTabs.teachersName'),
        icon: AcademicCapIcon,
        component: TeachersList,
        description: t('benefitsTabs.teachersDesc'),
      },
      {
        id: 'revenue',
        name: t('benefitsTabs.revenueName'),
        icon: ArrowTrendingUpIcon,
        component: RevenueAnalysisSimplified,
        description: t('benefitsTabs.revenueDesc'),
      },
      {
        id: 'expenses',
        name: t('benefitsTabs.expensesName'),
        icon: BanknotesIcon,
        component: ExpenseTracker,
        description: t('benefitsTabs.expensesDesc'),
      },
    ],
    [t]
  );

  const CurrentTabComponent =
    tabs.find((tab) => tab.id === activeTab)?.component;

  // Fetch current month's financial summary
  const currentDate = new Date();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Format dates for API
  const startDate = firstDayOfMonth.toISOString().split('T')[0];
  const endDate = lastDayOfMonth.toISOString().split('T')[0];
  
  const { data: dashboardData, isLoading, refetch: refetchFinancialSummary } = useQuery(
    [...BENEFITS_DASHBOARD_KEY, startDate, endDate],
    async () => {
      const data = await fetchBenefitsDashboardData();
      const summary = computePeriodFinancials(
        data.payments,
        data.expenses,
        firstDayOfMonth,
        lastDayOfMonth,
        data.commissionRate
      );
      return {
        ...data,
        summary: {
          totalIncome: summary.totalIncome,
          totalExpenses: summary.totalExpenses,
          netProfit: summary.netProfit,
          profitMargin: summary.profitMargin,
          activeStudents: data.activeStudents,
          activeCourses: data.activeCourses,
          totalPayments: summary.paymentsCount,
        },
      };
    },
    {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      onError: () => {
        toast.error('Impossible de charger le résumé financier');
      },
    }
  );

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await invalidateFormationGraph(queryClient);
      await refetchFinancialSummary();
      setLastUpdate(new Date());
      toast.success('Données actualisées avec succès!');
    } catch (error) {
      toast.error('Erreur lors de l\'actualisation');
    } finally {
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  };


  // Prepare stats from API data
  const stats = React.useMemo(() => {
    const summary = dashboardData?.summary || {
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
      profitMargin: 0
    };
    
    return [
      {
        title: 'Revenus Formations',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.totalIncome),
        icon: BanknotesIcon,
        iconWrap: 'bg-forma-500/15 text-forma-700 dark:bg-forma-500/20 dark:text-forma-200',
      },
      {
        title: 'Coûts Formation',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.totalExpenses),
        icon: CurrencyDollarIcon,
        iconWrap: 'bg-rose-500/15 text-rose-800 dark:bg-rose-500/20 dark:text-rose-100',
      },
      {
        title: 'Bénéfice Éducatif',
        value: new Intl.NumberFormat('fr-DZ', {
          style: 'currency',
          currency: 'DZD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(summary.netProfit),
        icon: CheckCircleIcon,
        iconWrap: 'bg-emerald-500/15 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-100',
      },
      {
        title: 'Rentabilité Formation',
        value: `${parseFloat(summary.profitMargin).toFixed(1)}%`,
        icon: ArrowTrendingUpIcon,
        iconWrap: 'bg-amber-500/15 text-amber-900 dark:bg-amber-500/20 dark:text-amber-100',
      },
    ];
  }, [dashboardData]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('benefitsPageTitle')}
        description={t('pageDescriptions.benefits')}
        actions={
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className={clsx(
              'inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800',
              isRefreshing && 'cursor-not-allowed opacity-60'
            )}
          >
            <ArrowPathIcon className={clsx('h-5 w-5', isRefreshing && 'animate-spin')} />
            {isRefreshing ? 'Actualisation…' : 'Actualiser'}
          </button>
        }
      />

      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
        <span className={isRefreshing ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}>
          {isRefreshing ? 'Actualisation…' : 'Données en temps réel'}
        </span>
        <span className="mx-2 text-slate-300 dark:text-slate-600">•</span>
        {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
        <span className="mx-2 text-slate-300 dark:text-slate-600">•</span>
        Dernière màj :{' '}
        {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-50 mt-2">
                  {isLoading ? '...' : stat.value}
                </p>
              </div>
              <div className={clsx('p-3 rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80', stat.iconWrap)}>
                <stat.icon className="h-6 w-6" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <UiCard padding={false} className="overflow-hidden">
        <div className="border-b border-slate-200 bg-white/50 px-2 py-2 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={clsx(
                'group relative flex min-w-max flex-1 flex-col items-center justify-center space-y-1 rounded-2xl px-5 py-3 text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-forma-600 text-white shadow-md shadow-forma-600/25'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800'
              )}
            >
              <div
                className={clsx(
                  'rounded-xl p-2 transition-all',
                  activeTab === tab.id ? 'bg-white/20' : 'bg-slate-100/80 dark:bg-slate-800/60'
                )}
              >
                <tab.icon className="h-6 w-6" />
              </div>
              <span className="whitespace-nowrap font-semibold">{tab.name}</span>
              <p className="line-clamp-2 hidden max-w-[12rem] text-center text-xs opacity-80 lg:block">
                {tab.description}
              </p>
            </button>
          ))}
        </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/90 px-6 py-4 dark:border-slate-600 dark:bg-slate-800/40">
          <div className="flex items-center space-x-3">
            {React.createElement(tabs.find(tab => tab.id === activeTab)?.icon || ChartPieIcon, {
              className: "h-6 w-6 text-forma-600 dark:text-forma-400"
            })}
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50">
                {tabs.find(tab => tab.id === activeTab)?.name}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                {tabs.find(tab => tab.id === activeTab)?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="relative p-6">
          {CurrentTabComponent ? (
            <CurrentTabComponent showAddForm={showAddForm} setShowAddForm={setShowAddForm} />
          ) : (
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
              {t('benefitsTabs.devFallback')}
            </div>
          )}
        </div>
      </UiCard>
    </div>
  );
};

export default BenefitsPage;